import { FC, useState } from "react";
import Table from "../ui/table";
import Pagination from "../ui/table/Pagination";
import { useSearch } from "../../hooks/useSearch";
import { IEntityChangeGroup } from "../../interfaces/endpoints/IEntityChangeLog.types";
import { getEntityChangeGroups } from "../../services/EntityChangeLog.service";
import ActionDetailsModal from "./ActionDetailsModal";
import ActionRow from "./ActionRow";
import ChangeDetailsModal from "./ChangeDetailsModal";
import { labelForEntity } from "./changeHistoryLabels";

const COLUMNS = ["Kur", "Çfarë", "Veprimi", "Kush", "Ndryshimet", ""];
const PAGE_SIZE = 20;

interface IHistoryTable {
  // Empty: every audited entity. Name only: every row of that type. Both: one entity and what was
  // written underneath it (a trip's stop times and prices).
  entityName: string;
  entityId: string;
  // Called when the user clicks an entity cell, to narrow the list to that entity.
  onPickEntity: (entityName: string, entityId: number) => void;
}

// The change history as the admin experienced it: one line per action (every row one request wrote),
// newest first. "Detajet" opens the record, or the list of records when the action wrote several.
const HistoryTable: FC<IHistoryTable> = ({ entityName, entityId, onPickEntity }) => {
  const [openLogId, setOpenLogId] = useState<number | null>(null);
  const [openAction, setOpenAction] = useState<IEntityChangeGroup | null>(null);
  const { response, loading, handlePageChange, page, refetch, query } = useSearch(getEntityChangeGroups, {
    // The search box in the page header writes ?searchkeyword=...; the backend matches ids, the
    // entity type, who made the change and the values inside it, and lists a matching action in full.
    withQuerySearch: true,
    otherQuery: {
      pageSize: String(PAGE_SIZE),
      ...(entityName && { entityName }),
      ...(entityId && { entityId })
    }
  });
  const groups: IEntityChangeGroup[] = response?.data?.result ?? [];
  const totalCount = response?.data?.totalCount ?? 0;

  const emptyText = query
    ? `Nuk u gjet asnjë ndryshim për "${query}"`
    : entityName
    ? `Nuk ka ndryshime të regjistruara për ${labelForEntity(entityName)}${entityId ? ` #${entityId}` : ""}`
    : "Nuk ka ndryshime të regjistruara";

  return (
    <>
      <Table titles={COLUMNS} style="hover" loading={loading}>
        {!loading && groups.length ? (
          groups.map((group) => (
            <ActionRow
              key={group.key}
              group={group}
              currentEntityName={entityName}
              currentEntityId={entityId}
              onPickEntity={onPickEntity}
              onOpenRow={setOpenLogId}
              onOpenAction={setOpenAction}
            />
          ))
        ) : (
          <tr>
            <td colSpan={COLUMNS.length} className="text-center py-4">
              {loading ? "Duke ngarkuar..." : emptyText}
            </td>
          </tr>
        )}
      </Table>
      <Pagination
        onPageChange={handlePageChange}
        totalCount={totalCount}
        length={groups.length}
        page={page}
        pageSize={PAGE_SIZE}
      />
      {/* One record hides the action list it was opened from; closing it comes back to that list. */}
      {openAction && openLogId === null && (
        <ActionDetailsModal
          group={openAction}
          onClose={() => setOpenAction(null)}
          onOpenRow={setOpenLogId}
          onReverted={() => {
            setOpenAction(null);
            void refetch();
          }}
        />
      )}
      {openLogId !== null && (
        <ChangeDetailsModal
          logId={openLogId}
          onBack={openAction ? () => setOpenLogId(null) : undefined}
          onClose={() => setOpenLogId(null)}
          onReverted={() => {
            setOpenLogId(null);
            setOpenAction(null);
            void refetch();
          }}
        />
      )}
    </>
  );
};

export default HistoryTable;
